import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Home,
  Building2,
  BedDouble,
  HardHat,
  CheckCircle2,
  ArrowRight,
  Sparkles
} from "lucide-react";

const services = [
  {
    id: "residential",
    icon: Home,
    title: "Residential Cleaning",
    subtitle: "Your Home, Our Priority",
    description: "Transform your home into a pristine sanctuary. Our residential cleaning services are tailored to meet the unique needs of each home, ensuring every corner sparkles.",
    image: "https://images.unsplash.com/photo-1630699144035-c0f6311ec482?crop=entropy&cs=srgb&fm=jpg&w=1200",
    checklist: [
      "Complete dusting of all surfaces and fixtures",
      "Thorough vacuuming and mopping of all floors",
      "Kitchen deep clean including appliances",
      "Bathroom sanitization and disinfection",
      "Bedroom organization and freshening",
      "Window sills and baseboards cleaning",
      "Trash removal and bag replacement",
      "Door handles and light switches sanitization"
    ],
    addOns: [
      "Interior window cleaning",
      "Oven and refrigerator deep clean",
      "Laundry and folding",
      "Closet organization",
      "Garage cleaning"
    ]
  },
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial Cleaning",
    subtitle: "Professional Spaces, Pristine Results",
    description: "Maintain a professional, spotless workplace that impresses clients and boosts employee productivity. We work around your schedule to minimize disruption.",
    image: "https://images.unsplash.com/photo-1770992176317-382f51aff35d?crop=entropy&cs=srgb&fm=jpg&w=1200",
    checklist: [
      "Reception and common area cleaning",
      "Individual office and workstation cleaning",
      "Conference room preparation",
      "Kitchen and break room sanitization",
      "Restroom deep cleaning and restocking",
      "Floor care - vacuuming, mopping, polishing",
      "Waste management and recycling",
      "High-touch surface disinfection"
    ],
    addOns: [
      "Carpet shampooing",
      "Window washing",
      "Post-event cleaning",
      "After-hours service",
      "Daily maintenance programs"
    ]
  },
  {
    id: "airbnb",
    icon: BedDouble,
    title: "Airbnb & Vacation Rental",
    subtitle: "5-Star Reviews, Every Time",
    description: "Quick turnaround cleaning that ensures your property is guest-ready. Our Airbnb specialists understand the importance of first impressions and consistent quality.",
    image: "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&w=1200",
    checklist: [
      "Complete property cleaning between guests",
      "Fresh linen change and bed making",
      "Kitchen reset and appliance cleaning",
      "Bathroom sanitization with hotel-grade products",
      "Amenity restocking and arrangement",
      "Living area staging and tidying",
      "Quick quality inspection",
      "Photo-ready presentation"
    ],
    addOns: [
      "Inventory management",
      "Welcome basket setup",
      "Same-day turnaround",
      "Key management",
      "Damage reporting"
    ]
  },
  {
    id: "renovation",
    icon: HardHat,
    title: "Post-Renovation Cleaning",
    subtitle: "From Construction to Perfection",
    description: "Expert removal of construction dust, debris, and residue. We reveal the beauty of your newly renovated space with our specialized post-construction cleaning process.",
    image: "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?crop=entropy&cs=srgb&fm=jpg&w=1200",
    checklist: [
      "Construction dust removal from all surfaces",
      "Paint splatter and adhesive removal",
      "Window and glass cleaning (inside & out)",
      "Cabinet and fixture cleaning",
      "Floor scrubbing and polishing",
      "HVAC vent and duct cleaning",
      "Light fixture and ceiling fan cleaning",
      "Final detail inspection"
    ],
    addOns: [
      "Pressure washing (exterior)",
      "Carpet installation cleaning",
      "Move-in preparation",
      "Multiple-phase cleaning",
      "Custom project timeline"
    ]
  }
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
              Our Services
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6">
              Premium Cleaning Services for Every Need
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/80">
              From residential homes to commercial spaces, Airbnb properties to post-renovation sites, we deliver exceptional cleaning tailored to your specific requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl space-y-32">
          {services.map((service, index) => (
            <div
              key={service.id}
              id={service.id}
              className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-16 items-center`}
              data-testid={`service-section-${service.id}`}
            >
              {/* Image */}
              <div className="w-full lg:w-1/2">
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#064E3B]/40 to-transparent" />
                  <div className="absolute bottom-6 left-6 flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-[#C0A062] flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2">
                <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-2">
                  {service.subtitle}
                </p>
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-4">
                  {service.title}
                </h2>
                <p className="text-lg text-[#78716C] mb-8 leading-relaxed">
                  {service.description}
                </p>

                <Accordion type="single" collapsible className="mb-8">
                  <AccordionItem value="checklist" className="border-[#E7E5E4]">
                    <AccordionTrigger className="text-[#064E3B] font-semibold hover:text-[#065F46] hover:no-underline">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" />
                        What's Included
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="grid grid-cols-1 gap-3 pt-4">
                        {service.checklist.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#064E3B] mt-0.5 flex-shrink-0" />
                            <span className="text-[#78716C]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="addons" className="border-[#E7E5E4]">
                    <AccordionTrigger className="text-[#064E3B] font-semibold hover:text-[#065F46] hover:no-underline">
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Available Add-Ons
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="grid grid-cols-1 gap-3 pt-4">
                        {service.addOns.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <Sparkles className="w-5 h-5 text-[#C0A062] mt-0.5 flex-shrink-0" />
                            <span className="text-[#78716C]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <Link to="/booking" data-testid={`book-${service.id}`}>
                  <Button className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-emerald-900/20 transition-all hover:scale-[1.02]">
                    Book This Service
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Contact us for a free consultation. We'll assess your needs and recommend the perfect cleaning solution for your space.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" data-testid="get-quote-btn">
              <Button className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-[#C0A062]/30">
                Get a Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/pricing" data-testid="view-pricing-btn">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#064E3B] rounded-full px-8 py-6 text-lg font-medium">
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
